import { useEffect, useRef } from "react";
import { Compass } from "lucide-react";
import {
  HOME_ENTER_EVENT,
  HOME_ENTER_PENDING_KEY,
  HOME_ROUTE_WAKE_EVENT,
} from "./homeScene";
import { useWorldExperience } from "../world/WorldExperienceProvider";

interface EnterVillageGatewayProps {
  onDirect: () => void;
}

export function EnterVillageGateway({ onDirect }: EnterVillageGatewayProps) {
  const { capability, setPreference, announceMode, rendererHealth } =
    useWorldExperience();
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const canEnterBalanced = capability.balancedAllowed && !reducedMotion;
  const interaction = useRef({ focused: false, hovered: false });

  useEffect(() => {
    if (rendererHealth === "failed")
      sessionStorage.removeItem(HOME_ENTER_PENDING_KEY);
  }, [rendererHealth]);

  useEffect(
    () => () => {
      window.dispatchEvent(
        new CustomEvent(HOME_ROUTE_WAKE_EVENT, { detail: { awake: false } }),
      );
    },
    [],
  );

  const publishRouteWake = () => {
    window.dispatchEvent(
      new CustomEvent(HOME_ROUTE_WAKE_EVENT, {
        detail: {
          awake: interaction.current.focused || interaction.current.hovered,
        },
      }),
    );
  };

  const enter = () => {
    if (!canEnterBalanced) {
      announceMode(
        reducedMotion
          ? "Reduced motion is active. Direct village access opened."
          : "Balanced world is unavailable. Direct village access opened.",
      );
      onDirect();
      return;
    }
    sessionStorage.setItem(HOME_ENTER_PENDING_KEY, "1");
    announceMode("Preparing the final three-dimensional cloud world.");
    setPreference("balanced");
    window.dispatchEvent(new Event(HOME_ENTER_EVENT));
  };

  return (
    <div className="home-gateway-wrap">
      <button
        className="home-enter-gateway"
        type="button"
        onClick={enter}
        onPointerEnter={() => {
          interaction.current.hovered = true;
          publishRouteWake();
        }}
        onPointerLeave={() => {
          interaction.current.hovered = false;
          publishRouteWake();
        }}
        onFocus={() => {
          interaction.current.focused = true;
          publishRouteWake();
        }}
        onBlur={() => {
          interaction.current.focused = false;
          publishRouteWake();
        }}
        aria-describedby="home-gateway-status"
      >
        <span className="home-gateway-orbit" aria-hidden="true">
          <i />
          <i />
          <i />
          <i />
        </span>
        <Compass aria-hidden="true" />
        <strong>ENTER THE VILLAGE</strong>
        <small>
          {canEnterBalanced
            ? "Begin the three-dimensional cloud descent"
            : "Open instant village access"}
        </small>
      </button>
      <span id="home-gateway-status" className="sr-only" role="status" aria-live="polite">
        {rendererHealth === "recovering"
          ? "The playable world is recovering."
          : rendererHealth === "failed"
            ? "The playable world is unavailable. Static access remains available."
            : ""}
      </span>
    </div>
  );
}
